import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";
import { Candles } from "@/components/site/Candles";
import { PageIntro } from "@/components/site/PageIntro";
import { Ladybug } from "@/components/site/Ladybug";

export const PROGRAMS = [
  ["clinic", "The Healers Clinic"],
  ["womb", "The Healing Womb"],
  ["cosmic", "The Cosmic Business Blueprint"],
  ["apprenticeship", "Holistic Home Care Practitioner Apprenticeship"],
  ["ambassadors", "The Healing Ambassadors"],
];

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-3">
    {["Yes", "No"].map((o) => (
      <button type="button" key={o} onClick={() => onChange(o)} data-testid={`${testid}-${o.toLowerCase()}`}
        className={`rounded-full px-6 py-2 text-sm border transition-colors duration-300 ${value === o ? "bg-[#b6689a] text-white border-[#b6689a]" : "border-[#dacac1] hover:border-[#b6689a]"}`}>
        {o}
      </button>
    ))}
  </div>
);

const WombQuestions = ({ form, set }) => (
  <div data-testid="womb-questions" className="rounded-3xl bg-[#b6689a]/10 border border-[#b6689a]/25 p-8 space-y-6 rise">
    <div>
      <h3 className="font-serif-h text-2xl text-[#b6689a]">The Healing Womb (additional questions)</h3>
      <p className="text-sm text-[#232b1b]/70 mt-1">Help us hold you well.</p>
    </div>
    <div><label className="label">What is your date of birth?</label><input type="date" className="field max-w-xs" value={form.dob} onChange={(e) => set("dob", e.target.value)} data-testid="womb-dob-input" /></div>
    <div><label className="label">Where are you located? <span className="text-[#232b1b]/55 font-normal">(city and state)</span></label><input className="field" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="womb-location-input" /></div>
    <div><label className="label">Are you currently postpartum?</label><YesNo value={form.postpartum} onChange={(v) => set("postpartum", v)} testid="womb-postpartum" /></div>
    <div><label className="label">How many children do you have?</label><input className="field max-w-xs" value={form.children} onChange={(e) => set("children", e.target.value)} data-testid="womb-children-input" /></div>
    <div><label className="label">Do you have a doula supporting you?</label><YesNo value={form.doula} onChange={(v) => set("doula", v)} testid="womb-doula" /></div>
  </div>
);

export default function Apply() {
  const [params] = useSearchParams();
  const initial = (params.get("program") || "").split(",").filter((p) => PROGRAMS.some(([k]) => k === p));
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", heard_about: "", programs: initial, dob: "", location: "", postpartum: "", children: "", doula: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k) => set("programs", form.programs.includes(k) ? form.programs.filter((p) => p !== k) : [...form.programs, k]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.programs.length) return setError("Please choose at least one path that is calling you.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/applications", form);
      setDone(data.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="apply-success" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <img src="/img/logo.jpg" alt="The Healers University" className="w-36 mx-auto mix-blend-multiply" />
        <h1 className="font-serif-h text-4xl sm:text-5xl text-[#577731] mt-8">{done}</h1>
        <p className="mt-6 text-lg text-[#232b1b]/75 leading-relaxed">
          Your first step has been taken, and it was heard. Kheru and the circle will sit with your words and reach out to you soon. Until then, rest, breathe, and know you are already held.
        </p>
        <Link to="/" data-testid="apply-success-home-btn" className="btn btn-ghost mt-10">Return Home</Link>
      </div>
    );
  }

  return (
    <div data-testid="apply-page">
      <PageIntro eyebrow="Application" title="Every journey begins with a single step. Tell us who you are." testid="apply-title" />
      <form onSubmit={submit} className="max-w-3xl mx-auto px-6 lg:px-10 space-y-7" data-testid="apply-form">
        <div><label className="label">Full name</label><input required className="field" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="apply-name-input" /></div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div><label className="label">Phone</label><input required className="field" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="apply-phone-input" /></div>
          <div><label className="label">Email</label><input required type="email" className="field" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email-input" /></div>
        </div>
        <div>
          <label className="label">How did the medicine find you? <span className="text-[#232b1b]/55 font-normal">(How did you hear about us?)</span></label>
          <textarea rows={3} className="field" value={form.heard_about} onChange={(e) => set("heard_about", e.target.value)} data-testid="apply-heard-input" />
        </div>
        <fieldset>
          <legend className="label">Which path is calling you? <span className="text-[#232b1b]/55 font-normal">(check all that apply)</span></legend>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {PROGRAMS.map(([k, label]) => (
              <label key={k} className={`flex items-center gap-3 rounded-2xl border px-5 py-4 cursor-pointer text-sm transition-colors duration-300 ${form.programs.includes(k) ? "border-[#577731] bg-[#b1cc90]/25" : "border-[#dacac1] bg-white/70 hover:border-[#577731]"}`}>
                <Checkbox checked={form.programs.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`apply-program-${k}`} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        {form.programs.includes("womb") && <WombQuestions form={form} set={set} />}
        {error && <p data-testid="apply-error" className="text-sm text-[#9c3d5c]">{error}</p>}
        <button disabled={busy} data-testid="apply-submit-btn" className="btn btn-olive">{busy ? "Sending with care…" : "Send My Application"} <Ladybug size={16} rotate={30} /></button>
      </form>
      <div className="mt-24 border-t border-[#dacac1]"><Candles /></div>
    </div>
  );
}
